import { supabase } from '../../shared/supabase';

class OfferService {
  /**
   * Create a new offer
   */
  async createOffer(offerData) {
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      const formattedData = {
        ...offerData,
        price: parseFloat(offerData.price),
        discounted_price: parseFloat(offerData.discounted_price),
        user_id: userData.user?.id || null,
        category: 'Offers',
      };

      const { data, error } = await supabase.from('fooditems').insert([formattedData]).select();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all offers for a specific restaurant
   */
  async getRestaurantOffers(restaurantId) {
    try {
      const { data, error } = await supabase
        .from('fooditems')
        .select('*')
        .eq('category', 'Offers')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all offers (keep existing function for backward compatibility)
   */
  async getAllOffers(restaurantId = null) {
    try {
      let query = supabase.from('fooditems').select('*').eq('category', 'Offers');

      if (restaurantId) {
        query = query.eq('restaurant_id', restaurantId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update offer
   */
  async updateOffer(id, offerData) {
    try {
      const formattedData = {
        ...offerData,
        price: parseFloat(offerData.price),
        discounted_price: parseFloat(offerData.discounted_price),
        category: 'Offers',
      };

      const { data, error } = await supabase
        .from('fooditems')
        .update(formattedData)
        .eq('id', id)
        .select();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete offer
   */
  async deleteOffer(id) {
    try {
      const { error: getError } = await supabase
        .from('fooditems')
        .select('image')
        .eq('id', id)
        .single();

      if (getError) {
        // safe ignore / log
      }

      const { error } = await supabase.from('fooditems').delete().eq('id', id);

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Set/unset individual offer carousel status
   */
  async setCarouselOffer(offerId, isCarousel) {
    try {
      const { data, error } = await supabase
        .from('fooditems')
        .update({ is_carousel: isCarousel })
        .eq('id', offerId)
        .select();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update featured carousel offers (alternative method if needed)
   */
  async setMultipleCarouselOffers(offerIds, restaurantId = null) {
    try {
      let resetQuery = supabase
        .from('fooditems')
        .update({ is_carousel: false })
        .eq('category', 'Offers');

      if (restaurantId) {
        resetQuery = resetQuery.eq('restaurant_id', restaurantId);
      }

      const { error: resetError } = await resetQuery;

      if (resetError) {
        throw resetError;
      }

      if (offerIds && offerIds.length > 0) {
        const { error: updateError } = await supabase
          .from('fooditems')
          .update({ is_carousel: true })
          .in('id', offerIds);

        if (updateError) {
          throw updateError;
        }
      }

      return true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all featured carousel offers for a specific restaurant
   */
  async getRestaurantCarouselOffers(restaurantId) {
    try {
      const { data, error } = await supabase
        .from('fooditems')
        .select('*')
        .eq('category', 'Offers')
        .eq('restaurant_id', restaurantId)
        .eq('is_carousel', true)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all featured carousel offers (keep existing function for backward compatibility)
   */
  async getCarouselOffers(restaurantId = null) {
    try {
      let query = supabase
        .from('fooditems')
        .select('*')
        .eq('category', 'Offers')
        .eq('is_carousel', true);

      if (restaurantId) {
        query = query.eq('restaurant_id', restaurantId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get offers by category for a specific restaurant
   */
  async getRestaurantOffersByCategory(restaurantId, category) {
    try {
      const { data, error } = await supabase
        .from('fooditems')
        .select('*')
        .eq('category', 'Offers')
        .eq('restaurant_id', restaurantId)
        .eq('subcategory', category)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Search offers for a specific restaurant
   */
  async searchRestaurantOffers(restaurantId, searchTerm) {
    try {
      const { data, error } = await supabase
        .from('fooditems')
        .select('*')
        .eq('category', 'Offers')
        .eq('restaurant_id', restaurantId)
        .or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get offer statistics for a specific restaurant
   */
  async getRestaurantOfferStats(restaurantId) {
    try {
      const { data, error } = await supabase
        .from('fooditems')
        .select('price, discounted_price, is_carousel')
        .eq('category', 'Offers')
        .eq('restaurant_id', restaurantId);

      if (error) {
        throw error;
      }

      const stats = {
        totalOffers: data.length,
        carouselOffers: data.filter((offer) => offer.is_carousel).length,
        totalSavings: data.reduce((sum, offer) => {
          return sum + (parseFloat(offer.price) - parseFloat(offer.discounted_price));
        }, 0),
        averageDiscount:
          data.length > 0
            ? data.reduce((sum, offer) => {
                const discount =
                  ((parseFloat(offer.price) - parseFloat(offer.discounted_price)) /
                    parseFloat(offer.price)) *
                  100;
                return sum + discount;
              }, 0) / data.length
            : 0,
      };

      return stats;
    } catch (error) {
      throw error;
    }
  }

  async createCompositeOfferImage(images) {
    return images[0];
  }
}

// Instantiate singleton
export const offerService = new OfferService();

// Backwards-compatible bindings
export const createOffer = offerService.createOffer.bind(offerService);
export const getRestaurantOffers = offerService.getRestaurantOffers.bind(offerService);
export const getAllOffers = offerService.getAllOffers.bind(offerService);
export const updateOffer = offerService.updateOffer.bind(offerService);
export const deleteOffer = offerService.deleteOffer.bind(offerService);
export const setCarouselOffer = offerService.setCarouselOffer.bind(offerService);
export const setMultipleCarouselOffers = offerService.setMultipleCarouselOffers.bind(offerService);
export const getRestaurantCarouselOffers =
  offerService.getRestaurantCarouselOffers.bind(offerService);
export const getCarouselOffers = offerService.getCarouselOffers.bind(offerService);
export const getRestaurantOffersByCategory =
  offerService.getRestaurantOffersByCategory.bind(offerService);
export const searchRestaurantOffers = offerService.searchRestaurantOffers.bind(offerService);
export const getRestaurantOfferStats = offerService.getRestaurantOfferStats.bind(offerService);
export const createCompositeOfferImage = offerService.createCompositeOfferImage.bind(offerService);

export default {
  createOffer,
  getRestaurantOffers,
  getAllOffers,
  updateOffer,
  deleteOffer,
  setCarouselOffer,
  setMultipleCarouselOffers,
  getRestaurantCarouselOffers,
  getCarouselOffers,
  getRestaurantOffersByCategory,
  searchRestaurantOffers,
  getRestaurantOfferStats,
  createCompositeOfferImage,
};
